import React from 'react'
import { useParams } from 'react-router'
import { Divider, Group, NumberInput, Stack, Text, Title } from '@mantine/core'
import { RadioCard, SearchablePillInput } from '../../../common'
import { useMarketElements, usePricingVersionCache, useUpdateVersionRules, useVersionRulesCache } from '../../../../hooks/global-job-matching'
import { Methodology as Method } from '../../GlobalJobMatchingVersioning/types/rules'

export const Methodology = () => {
  const params = useParams<{ id: string }>()
  const rules = useVersionRulesCache(params.id)
  const version = usePricingVersionCache(params.id)
  const { data: marketElements } = useMarketElements()
  const { mutate: updateRules } = useUpdateVersionRules(params.id)

  const handleParticipationChange = (val: string | number, target: 'incumbentsMinimum' | 'companiesMinimum') => {
    const updatedValue = val === '' ? null : Number(val)
    updateRules({ [target]: updatedValue })
  }

  const handleMarketElementSelect = (newValues: string[]) => {
    const updatedValues: string[] = []
    newValues.forEach(val => {
      const sourceField = marketElements?.payElements?.find(el => el?.field_title === val)?.source_field
      if (sourceField) updatedValues.push(sourceField)
    })
    updateRules({ marketDataElements: updatedValues })
  }

  const selectedElements: string[] = []
  rules.marketDataElements?.forEach(sourceField => {
    const fieldTitle = marketElements?.payElements?.find(marketElement => marketElement?.source_field === sourceField)?.field_title
    if (fieldTitle) selectedElements.push(fieldTitle)
  })

  return (
    <Stack p='xl' h='calc(100vh - 300px)' style={{ overflow: 'scroll' }}>
      <Title order={2} fw={600}>Set your Methodology</Title>
      <Text size='lg'>Refine the methodology used in generating your matches</Text>
      <Divider my='lg' />
      { version.matchStrategy === 'file' &&
      <>
        <Title order={2} fw={600}>What priorities do you want to use?</Title>
        <Text size='lg'>Choose between Match priorities or Data Cut priorities</Text>
        <Group wrap='nowrap'>
          <RadioCard
            label='Match priorities'
            checked={rules?.methodology === Method.MATCH_PRIORITY}
            onClick={() => updateRules({ methodology: Method.MATCH_PRIORITY })}
            description='For each survey, the match with the highest match priority will be used, followed by data cut priority.'
          />
          <RadioCard
            label='Data cut priorities'
            checked={rules?.methodology === Method.DATA_CUT_PRIORITY}
            onClick={() => updateRules({ methodology: Method.DATA_CUT_PRIORITY })}
            description='For each survey, the match with the highest data cut priority will be used, followed by match priority.'
          />
        </Group>
      </>
      }
      <Title order={2} fw={600} mt='xl'>Participation Threshold</Title>
      <Text size='lg'>Set your minimum threshold for both incumbents and companies</Text>
      <Group>
        <NumberInput
          label='Minimum incumbents'
          placeholder='e.g. 500'
          value={rules?.incumbentsMinimum}
          allowNegative={false}
          allowDecimal={false}
          allowLeadingZeros={false}
          onChange={val => handleParticipationChange(val, 'incumbentsMinimum')}
        />
        <NumberInput
          label='Minimum companies'
          placeholder='e.g. 500'
          value={rules?.companiesMinimum}
          allowNegative={false}
          allowDecimal={false}
          allowLeadingZeros={false}
          onChange={val => handleParticipationChange(val, 'companiesMinimum')}
        />
      </Group>
      <Divider my='lg' />
      <Title order={2} fw={600}>Required market data elements for pricing results</Title>
      <Text size='lg'>Select the market data elements needed for pricing outcomes</Text>
      <SearchablePillInput
        placeholder='Type to search'
        items={marketElements?.payElements.map(el => el.field_title) || []}
        selected={selectedElements}
        onSelect={handleMarketElementSelect}
        onRemove={handleMarketElementSelect}
      />
    </Stack>
  )
}
